import { useCallback, useEffect, useRef } from "react";

/**
 * Makes the Android / browser hardware back button close an open dialog
 * instead of navigating away from the page.
 *
 * In a plain mobile web app there is no way to intercept the hardware back
 * button directly — the only lever is the History API. While a dialog is open
 * we push one synthetic history entry on the *same* URL (so the router does
 * not navigate). Pressing back pops that entry and fires `popstate`, which we
 * translate into the dialog's close handler. Closing the dialog by any other
 * means (close button, backdrop, an action, unmount) drains the synthetic
 * entry again so the history stack stays clean.
 *
 * Nested dialogs work as a LIFO stack: each open dialog owns one entry, so
 * back closes the top-most dialog first, then the next, before navigating.
 *
 * @param {boolean} open    Whether the dialog is currently open.
 * @param {Function} onClose Called when the back button should close it.
 */

// Module-level state shared by every dialog on the page. A single popstate
// listener drives all of them, which avoids several listeners reacting to the
// same back press.
const dialogStack = [];
// Number of upcoming popstate events that were triggered by our own
// history.back() draining and must therefore be ignored.
let suppressCount = 0;
let listening = false;
let idCounter = 0;

const DIALOG_STATE_KEY = "__dialogBackButton";

function handlePopState() {
  if (suppressCount > 0) {
    suppressCount -= 1;
    return;
  }
  const entry = dialogStack.pop();
  if (!entry) {
    return;
  }
  // The browser already removed this dialog's synthetic history entry, so
  // closing it must not try to drain anything afterwards.
  entry.consumedByBack = true;
  entry.close();
}

function ensureListening() {
  if (!listening) {
    window.addEventListener("popstate", handlePopState);
    listening = true;
  }
}

function pushDialog(close) {
  ensureListening();
  const entry = { id: ++idCounter, close, consumedByBack: false };
  dialogStack.push(entry);
  // Same URL, so the router stays on the current route; we only tag the entry
  // and preserve whatever state the router already stored.
  window.history.pushState(
    { ...window.history.state, [DIALOG_STATE_KEY]: entry.id },
    "",
    window.location.href,
  );
  return entry;
}

function popDialog(entry) {
  const index = dialogStack.indexOf(entry);
  if (index !== -1) {
    dialogStack.splice(index, 1);
  }

  if (entry.consumedByBack) {
    // Already closed by the back button; its history entry is gone.
    return;
  }

  // Closed by other means. Only drain if our synthetic entry is still the
  // current one — if something navigated on top of it (e.g. a link inside the
  // dialog) going back would undo that navigation, so we leave the (harmless,
  // same-URL) entry buried instead.
  const state = window.history.state;
  if (state && state[DIALOG_STATE_KEY] === entry.id) {
    suppressCount += 1;
    window.history.back();
  }
}

export default function useDialogBackButton(open, onClose) {
  const onCloseRef = useRef(onClose);
  const entryRef = useRef(null);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      entryRef.current = null;
      return undefined;
    }

    const entry = pushDialog(() => {
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    });
    entryRef.current = entry;

    return () => popDialog(entry);
  }, [open]);

  const consumeClose = useCallback(() => {
    const entry = entryRef.current;
    if (!entry) {
      return;
    }

    entry.consumedByBack = true;
    const index = dialogStack.indexOf(entry);
    if (index !== -1) {
      dialogStack.splice(index, 1);
    }
    entryRef.current = null;

    if (onCloseRef.current) {
      onCloseRef.current();
    }
  }, []);

  return { consumeClose };
}
